import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid } from "@mui/material";

import * as AppConstants from "AppConstants";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getDateInDDMMYYYYFormat } from "utils/dateUtil";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import { resolveOwnerParams } from "utils/effortOwnerParams";
import { LeavePlansBetweenDates } from "app/components/leave-plans/LeavePlansBetweenDates";

/**
 * The Leave Plan Summary card: its own Client / Member / Period filter over the leave grid.
 *
 * Self-contained on purpose, mirroring EffortSummaryView: the three dashboards each held their
 * own copy of this Card markup and would otherwise need a second pending/applied state machine
 * apiece to gain a filter. Owning the filter and the scope here means one place resolves scope
 * for every role.
 *
 * The caller supplies the raw member/manager records — NOT lists already narrowed for another
 * filter — because the option lists are derived here against this card's own client selection.
 * @since NEXT_VERSION
 */
export const LeavePlanSummaryCard = ({
  idPrefix,
  role,
  context,
  showClient = false,
  clientOptions = [],
  showUser = false,
  groupUsers = true,
  members = [],
  managers = [],
  self = null,
  selfManagerIds = [],
}) => {
  const [pendingClientId, setPendingClientId] = useState(AppConstants.ALL_CLIENTS);
  const [pendingUser, setPendingUser] = useState(AppConstants.ALL_USERS);
  const [pendingPeriod, setPendingPeriod] = useState(defaultPeriod);

  // Applied as one object so a single Apply cannot leave the range, client and person
  // momentarily disagreeing about what the grid is showing.
  const [applied, setApplied] = useState(() => ({
    range: periodRange(defaultPeriod()),
    clientId: AppConstants.ALL_CLIENTS,
    user: AppConstants.ALL_USERS,
  }));

  // Counts Apply presses. The grid refetches on the resolved filter's VALUE, so an Apply that
  // changes no dropdown would otherwise be a no-op and leave newly added leave invisible until
  // some other input changed. Bumping this on every press makes Apply always mean "fetch now".
  //
  // Deliberately NOT a field of `applied`: that object is the snapshot of what the grid is
  // showing, and a press counter is scheduling metadata rather than part of what is shown
  // (HRMS-264 review). Both are set in the same handler, so React commits them together.
  const [reloadToken, setReloadToken] = useState(0);

  // A person from the previous client should not stay selected against a client they
  // do not belong to.
  const onClientChange = (value) => {
    setPendingClientId(value);
    setPendingUser(AppConstants.ALL_USERS);
  };

  const onApply = () => {
    setApplied({
      range: periodRange(pendingPeriod),
      clientId: pendingClientId,
      user: pendingUser,
    });
    // Updater form, not `reloadToken + 1`: two presses batched into one tick would otherwise
    // collapse to a single bump and the second press would be the no-op this ticket is about.
    setReloadToken((prev) => prev + 1);
  };

  const optionMembers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? members
        : members.filter((m) => String(m.client?.id ?? m.clientId) === String(pendingClientId)),
    [members, pendingClientId]
  );
  const optionManagers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? managers
        : managers.filter((m) => String(m.clientId) === String(pendingClientId)),
    [managers, pendingClientId]
  );
  const { memberOptions, managerOptions, selfOption } = useUserFilterOptions({
    members: optionMembers,
    managers: optionManagers,
    self,
  });

  const selection = useMemo(
    () => ({
      clientId: applied.clientId === AppConstants.ALL_CLIENTS ? null : applied.clientId,
      ownerParams: resolveOwnerParams(applied.user, members, selfManagerIds, managers),
    }),
    [applied.clientId, applied.user, members, managers, selfManagerIds]
  );

  return (
    <Card>
      <MDBox mx={2} mt={-3} py={1} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
        <MDTypography variant="h6" color="white">
          {`Leave plan summary for period - ${getDateInDDMMYYYYFormat(
            applied.range.from
          )} and ${getDateInDDMMYYYYFormat(applied.range.to)}`}
        </MDTypography>
      </MDBox>
      <MDBox px={2} pt={3}>
        <EffortFilterBar
          idPrefix={idPrefix}
          showClient={showClient}
          clientValue={pendingClientId}
          onClientChange={onClientChange}
          clientOptions={clientOptions}
          showUser={showUser}
          userValue={pendingUser}
          onUserChange={setPendingUser}
          memberOptions={memberOptions}
          managerOptions={managerOptions}
          selfOption={selfOption}
          groupUsers={groupUsers}
          period={pendingPeriod}
          onPeriodChange={setPendingPeriod}
          onAction={onApply}
          actionLabel="Search"
        />
      </MDBox>
      <Grid container spacing={2}>
        <Grid size={12}>
          <LeavePlansBetweenDates
            role={role}
            context={context}
            fromDate={applied.range.from}
            toDate={applied.range.to}
            selection={selection}
            reloadToken={reloadToken}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

LeavePlanSummaryCard.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
};

export default LeavePlanSummaryCard;
